import type { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { DataTableColumnHeader } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CostValue } from "@/components/recipes/cost/CostValue";
import type { RecipeIngredient } from "@/types/recipe";
import { ingredientLineName } from "@/types/recipe";
import type { CostedLine } from "@/lib/api/requests/recipes";

/**
 * Columns for the ingredient lines of a single recipe (the detail page's
 * Ingredients tab). Distinct from `recipes/tables/ingredients/columns.tsx`,
 * which describes the global ingredient catalogue.
 */

/** Shared header/cell padding — `meta.className` lands on both `th` and `td`. */
const CELL = "h-auto px-3 py-3";

/** The header text treatment, applied through `DataTableColumnHeader`. */
const HEAD = "text-xs font-medium text-(--color-text-secondary)";

const TEXT = "text-sm text-(--color-text-primary)";
const MUTED = "text-sm text-(--color-text-secondary)";

interface IngredientColumnOptions {
  /**
   * Costs from the recipe rollup, keyed by ingredient id. Missing entries mean
   * the rollup has not resolved that line yet — not that it is free.
   */
  costByIngredientId: Map<number, CostedLine>;
  onEdit: (ingredient: RecipeIngredient) => void;
  onRemove: (ingredient: RecipeIngredient) => void;
}

export function getRecipeIngredientColumns(
  i18n: I18n,
  { costByIngredientId, onEdit, onRemove }: IngredientColumnOptions,
): ColumnDef<RecipeIngredient, unknown>[] {
  /** `RID` is positional, so it is derived from the row index, not the data. */
  const rid = (index: number) => `ING-${String(index + 1).padStart(3, "0")}`;

  return [
    {
      id: "rid",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`RID`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-xs text-(--color-text-secondary)">
          {rid(row.index)}
        </span>
      ),
      enableSorting: false,
      meta: { label: i18n._(msg`RID`), className: CELL },
    },
    {
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Status`)}
          className={HEAD}
        />
      ),
      cell: () => (
        <div className="size-2.5 rounded-full bg-(--color-surface-accent)" />
      ),
      enableSorting: false,
      meta: { label: i18n._(msg`Status`), className: CELL },
    },
    {
      id: "groupName",
      accessorFn: (row) => row.groupName ?? "",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Group Name`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => (
        <span className={TEXT}>{row.original.groupName || "—"}</span>
      ),
      meta: { label: i18n._(msg`Group Name`), className: CELL },
    },
    {
      id: "subGroup",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Sub-Group`)}
          className={HEAD}
        />
      ),
      // No sub-group on the model yet; the column is a placeholder the design
      // asks for. Kept as an em dash rather than dropped so the layout matches.
      cell: () => <span className={MUTED}>—</span>,
      enableSorting: false,
      meta: { label: i18n._(msg`Sub-Group`), className: CELL },
    },
    {
      id: "prepAction",
      accessorFn: (row) => row.preparationNotes ?? "",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Prep Action`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => (
        <span className={TEXT}>{row.original.preparationNotes || "—"}</span>
      ),
      enableSorting: false,
      meta: { label: i18n._(msg`Prep Action`), className: CELL },
    },
    {
      id: "rawMaterial",
      // Not `material?.name`: a sub-recipe line has no material.
      accessorFn: (row) => ingredientLineName(row),
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Raw Material`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => (
        <span className="text-sm font-medium text-(--color-text-secondary)">
          {ingredientLineName(row.original) || "—"}
        </span>
      ),
      meta: { label: i18n._(msg`Raw Material`), className: CELL },
    },
    {
      id: "unitCost",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Unit Cost`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => {
        const cost = costByIngredientId.get(row.original.id) ?? null;

        return (
          <CostValue
            value={cost?.unitCost ?? null}
            reason={cost?.unavailableReason}
            detail={cost?.unavailableDetail}
          />
        );
      },
      enableSorting: false,
      meta: { label: i18n._(msg`Unit Cost`), className: CELL },
    },
    {
      id: "quantity",
      accessorFn: (row) => row.quantity,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Quantity`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => <span className={TEXT}>{row.original.quantity}</span>,
      meta: { label: i18n._(msg`Quantity`), className: CELL },
    },
    {
      id: "unit",
      accessorFn: (row) => row.unit ?? "",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`UOM`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => (
        <span className={TEXT}>{row.original.unit || "—"}</span>
      ),
      enableSorting: false,
      meta: { label: i18n._(msg`UOM`), className: CELL },
    },
    {
      id: "totalCost",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Total Cost`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => {
        const cost = costByIngredientId.get(row.original.id) ?? null;

        return (
          <CostValue
            value={cost?.extendedCost ?? null}
            reason={cost?.unavailableReason}
            detail={cost?.unavailableDetail}
          />
        );
      },
      enableSorting: false,
      meta: { label: i18n._(msg`Total Cost`), className: CELL },
    },
    {
      id: "allergens",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Allergens`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => {
        const allergens = row.original.material?.allergens ?? [];
        if (allergens.length === 0) return <span className={MUTED}>—</span>;

        return (
          <div className="flex flex-wrap gap-1">
            {allergens.map((a) => (
              <span
                key={a.id}
                className="inline-flex items-center rounded-full bg-(--color-warning-bg) px-1.5 py-0.5 text-[10px] font-medium text-(--color-warning-text)"
              >
                {a.allergen?.code}
              </span>
            ))}
          </div>
        );
      },
      enableSorting: false,
      meta: { label: i18n._(msg`Allergens`), className: CELL },
    },
    {
      id: "sequence",
      accessorFn: (row) => row.sortOrder,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n._(msg`Sequence`)}
          className={HEAD}
        />
      ),
      cell: ({ row }) => <span className={TEXT}>{row.original.sortOrder}</span>,
      meta: { label: i18n._(msg`Sequence`), className: CELL },
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-[6px] text-(--color-text-secondary) hover:text-(--color-text-primary)"
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem
              className="gap-2 cursor-pointer"
              onSelect={() => onEdit(row.original)}
            >
              <Pencil className="size-4" />
              {i18n._(msg`Edit`)}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 cursor-pointer text-(--color-danger-text) focus:text-(--color-danger-text)"
              onSelect={() => onRemove(row.original)}
            >
              <Trash2 className="size-4" />
              {i18n._(msg`Remove`)}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableSorting: false,
      enableHiding: false,
      meta: { className: `${CELL} w-10` },
    },
  ];
}
